import React, { useState, useMemo } from "react";
import PropTypes from "prop-types";
import {
  Card,
  Box,
  Typography,
  Grid,
  Stack,
  IconButton,
  Tooltip,
  Divider,
} from "@mui/material";
import {
  ContentCopy as CopyIcon,
  Check as CheckIcon,
  OpenInNew as OpenInNewIcon,
  CasinoOutlined as LotteryIcon,
  PersonOutline as CreatorIcon,
  CalendarTodayOutlined as CalendarIcon,
  ConfirmationNumberOutlined as TicketIcon,
  GroupOutlined as CapacityIcon,
  PercentOutlined as PercentIcon,
  TimerOutlined as TimerIcon,
  AccountBalanceWalletOutlined as PrizeIcon,
  EmojiEventsOutlined as TrophyIcon,
  AutoAwesome as SparkleIcon,
} from "@mui/icons-material";
import moment from "moment";
import { colors, borderRadiusPx, customShadows } from "../../../../theme";
import {
  LOTTERY_TYPES,
  STATUS_CONFIG,
  formatRolloverDuration,
  safeFromWei,
} from "./AdminLotteryCard";
import { CommonChip } from "../../ui";
import {
  getNetworkCurrency,
  lotteryTypeMapper,
  EXPLORER_LABEL,
  getExplorerAddressUrl,
} from "../../../../static/Constants";

/**
 * LotteryDetailsCard Component
 */
const LotteryDetailsCard = ({ lottery, datelist, winningNumbers }) => {
  const [copiedOwner, setCopiedOwner] = useState(false);
  const [copiedWinner, setCopiedWinner] = useState(false);

  const typeConfig = LOTTERY_TYPES[lottery?.lotteryType] || LOTTERY_TYPES[0];
  const TypeIcon = typeConfig.icon;
  const statusObj = STATUS_CONFIG[lottery?.status] || STATUS_CONFIG[0];

  const handleCopy = (text, type) => {
    if (!text) return;
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text);
      if (type === "owner") {
        setCopiedOwner(true);
        setTimeout(() => setCopiedOwner(false), 2000);
      } else {
        setCopiedWinner(true);
        setTimeout(() => setCopiedWinner(false), 2000);
      }
    }
  };

  const deployedDateFormatted = lottery?.deployedOn
    ? moment.unix(Number(lottery.deployedOn)).format("DD/MM/YYYY hh:mma")
    : "N/A";

  const startDateFormatted = datelist?.startTime
    ? moment.unix(Number(datelist.startTime)).format("DD/MM/YYYY hh:mma")
    : deployedDateFormatted;

  const drawDateFormatted = datelist?.drawTime
    ? moment.unix(Number(datelist.drawTime)).format("DD/MM/YYYY hh:mma")
    : "N/A";

  const lotteryIdFormatted = lottery?.deployedOn
    ? `${moment.unix(Number(lottery.deployedOn)).format("DDMMYYYY")} - ${lottery?.lotteryId}`
    : String(lottery?.lotteryId || "");

  const entryFeeInPol = safeFromWei(lottery?.entryFee);
  const totalPrizeInPol = safeFromWei(lottery?.totalPrize);
  const currency = getNetworkCurrency(lottery?.networkId);

  const hasWinner =
    lottery?.lotteryWinner &&
    lottery.lotteryWinner !== "0x0000000000000000000000000000000000000000";

  // Format winning number array
  const formattedWinningNumbers = useMemo(() => {
    if (!winningNumbers || (Array.isArray(winningNumbers) && winningNumbers.length === 0)) {
      return [];
    }
    if (Array.isArray(winningNumbers)) {
      return winningNumbers
        .map((n) => (typeof n === "object" ? "" : String(n).trim()))
        .filter(Boolean);
    }
    if (typeof winningNumbers === "string") {
      const cleanStr = winningNumbers.trim();
      if (cleanStr.includes("-")) {
        return cleanStr.split("-").map((s) => s.trim()).filter(Boolean);
      }
      if (cleanStr.includes(",")) {
        return cleanStr.split(",").map((s) => s.trim()).filter(Boolean);
      }
      return cleanStr ? [cleanStr] : [];
    }
    if (typeof winningNumbers === "number") {
      return [String(winningNumbers)];
    }
    return [String(winningNumbers)];
  }, [winningNumbers]);
  // Symmetrical row pairs to ensure left and right sections have matching heights
  const fieldPairs = [
    // Row 1: Created By & Lottery Type
    {
      left: {
        label: "Created By",
        icon: <CreatorIcon sx={{ fontSize: 18, color: colors.slate[500] }} />,
        value: (
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Typography
              sx={{
                fontFamily: "monospace",
                fontSize: "13px",
                fontWeight: 600,
                color: colors.slate[800],
              }}
            >
              {lottery?.ownerAddress
                ? `${lottery.ownerAddress.slice(0, 10)}...${lottery.ownerAddress.slice(-8)}`
                : "0x000...000"}
            </Typography>
            <Tooltip title={copiedOwner ? "Copied!" : "Copy Creator Address"} arrow>
              <IconButton
                size="small"
                onClick={() => handleCopy(lottery?.ownerAddress, "owner")}
                sx={{
                  p: 0.5,
                  color: copiedOwner ? colors.emerald[600] : colors.slate[400],
                  "&:hover": { color: colors.primary.main, bgcolor: colors.blue[50] },
                }}
              >
                {copiedOwner ? <CheckIcon sx={{ fontSize: 16 }} /> : <CopyIcon sx={{ fontSize: 16 }} />}
              </IconButton>
            </Tooltip>
            {lottery?.ownerAddress && (
              <Tooltip title={EXPLORER_LABEL} arrow>
                <IconButton
                  size="small"
                  component="a"
                  href={getExplorerAddressUrl(lottery.ownerAddress)}
                  target="_blank"
                  rel="noreferrer"
                  sx={{
                    p: 0.5,
                    color: colors.slate[400],
                    "&:hover": { color: colors.primary.main, bgcolor: colors.blue[50] },
                  }}
                >
                  <OpenInNewIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        ),
      },
      right: {
        label: "Lottery Type",
        icon: <TypeIcon sx={{ fontSize: 18, color: typeConfig.color }} />,
        value: (
          <CommonChip
            label={typeConfig.name}
            variant="default"
            size="small"
            sx={{
              backgroundColor: typeConfig.bgColor,
              color: typeConfig.color,
              fontWeight: 700,
              border: `1px solid ${typeConfig.color}30`,
            }}
          />
        ),
      },
    },

    // Row 2: Id & Status
    {
      left: {
        label: "Id",
        icon: <TicketIcon sx={{ fontSize: 18, color: colors.slate[500] }} />,
        value: (
          <Typography sx={{ fontWeight: 700, color: colors.slate[900], fontSize: "14px" }}>
            {lotteryIdFormatted}
          </Typography>
        ),
      },
      right: {
        label: "Status",
        icon: <TicketIcon sx={{ fontSize: 18, color: statusObj.color }} />,
        value: (
          <CommonChip
            label={statusObj.label}
            variant={statusObj.variant}
            size="small"
          />
        ),
      },
    },

    // Row 3: Total Numbers (Capacity) & Start Date
    {
      left: {
        label: "Total Numbers",
        icon: <CapacityIcon sx={{ fontSize: 18, color: colors.slate[500] }} />,
        value: (
          <Typography sx={{ fontWeight: 700, color: colors.slate[900], fontSize: "14px" }}>
            {String(lottery?.lotteryType) === "3"
              ? `N/A (${lotteryTypeMapper[3]} 3-Digit)`
              : lottery?.capacity || "N/A"}
          </Typography>
        ),
      },
      right: {
        label: "Start Date",
        icon: <CalendarIcon sx={{ fontSize: 18, color: colors.slate[500] }} />,
        value: (
          <Typography sx={{ fontWeight: 500, color: colors.slate[700], fontSize: "14px" }}>
            {startDateFormatted}
          </Typography>
        ),
      },
    },

    // Row 4: Total Numbers to Pick & Rollover Date
    {
      left: {
        label: "Total Numbers to Pick",
        icon: <TicketIcon sx={{ fontSize: 18, color: colors.slate[500] }} />,
        value: (
          <Typography sx={{ fontWeight: 700, color: colors.slate[900], fontSize: "14px" }}>
            {lottery?.pickNumbers || "1"}
          </Typography>
        ),
      },
      right: {
        label: "Rollover Date",
        icon: <TimerIcon sx={{ fontSize: 18, color: colors.slate[500] }} />,
        value: (
          <Typography sx={{ fontWeight: 500, color: colors.slate[700], fontSize: "14px" }}>
            {drawDateFormatted}
          </Typography>
        ),
      },
    },

    // Row 5: Entry Fee & Winner Amount
    {
      left: {
        label: "Entry Fee",
        icon: <PrizeIcon sx={{ fontSize: 18, color: colors.slate[500] }} />,
        value: (
          <Typography sx={{ fontWeight: 700, color: colors.primary.main, fontSize: "14px" }}>
            {entryFeeInPol ? `${entryFeeInPol} ${currency}` : `0 ${currency}`}
          </Typography>
        ),
      },
      right: {
        label: "Winner Amount",
        icon: <PrizeIcon sx={{ fontSize: 18, color: colors.emerald[600] }} />,
        value: (
          <Typography sx={{ fontWeight: 800, color: colors.emerald[600], fontSize: "15px" }}>
            {totalPrizeInPol ? `${totalPrizeInPol} ${currency}` : `0 ${currency}`}
          </Typography>
        ),
      },
    },

    // Row 6: Rollover & Rollover Duration
    {
      left: {
        label: "Rollover",
        icon: <PercentIcon sx={{ fontSize: 18, color: colors.slate[500] }} />,
        value: (
          <Typography sx={{ fontWeight: 700, color: colors.slate[900], fontSize: "14px" }}>
            {String(lottery?.lotteryType) === "1" ? `0% (${lotteryTypeMapper[1]})` : `${lottery?.rolloverperct || 0}%`}
          </Typography>
        ),
      },
      right: {
        label: "Rollover Duration",
        icon: <TimerIcon sx={{ fontSize: 18, color: colors.slate[500] }} />,
        value: (
          <Typography sx={{ fontWeight: 600, color: colors.slate[800], fontSize: "14px" }}>
            {formatRolloverDuration(datelist?.rolloverdays)}
          </Typography>
        ),
      },
    },

    // Row 7: Partners Share & Minimum Players
    {
      left: {
        label: "Partners Share",
        icon: <PercentIcon sx={{ fontSize: 18, color: colors.slate[500] }} />,
        value: (
          <Typography sx={{ fontWeight: 700, color: colors.slate[900], fontSize: "14px" }}>
            {lottery?.partnershare || 0}%
          </Typography>
        ),
      },
      right: {
        label: "Minimum Players",
        icon: <CapacityIcon sx={{ fontSize: 18, color: colors.slate[500] }} />,
        value: (
          <Typography sx={{ fontWeight: 700, color: colors.slate[900], fontSize: "14px" }}>
            {String(lottery?.lotteryType) === "2"
              ? `${lottery?.minPlayers || 0} Players`
              : "1 Player (No Minimum)"}
          </Typography>
        ),
      },
    },

    // Row 8: Winner Number & Creation Date
    {
      left: {
        label: "Winner Number",
        icon: <SparkleIcon sx={{ fontSize: 18, color: colors.amber[600] }} />,
        value:
          formattedWinningNumbers.length > 0 ? (
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap alignItems="center">
              {formattedWinningNumbers.map((num, i) => (
                <Box
                  key={i}
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: "50%",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 700,
                    fontSize: "12px",
                    background: "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
                    color: "#ffffff",
                    boxShadow: "0 2px 6px rgba(37, 99, 235, 0.3)",
                  }}
                >
                  {num}
                </Box>
              ))}
            </Stack>
          ) : hasWinner || Number(lottery?.status) === 2 ? (
            <Typography sx={{ fontWeight: 600, color: colors.emerald[600], fontSize: "13px" }}>
              Winner Selected
            </Typography>
          ) : (
            <Typography sx={{ fontWeight: 500, color: colors.slate[500], fontSize: "13px" }}>
              In progress
            </Typography>
          ),
      },
      right: {
        label: "Creation Date",
        icon: <CalendarIcon sx={{ fontSize: 18, color: colors.slate[500] }} />,
        value: (
          <Typography sx={{ fontWeight: 500, color: colors.slate[700], fontSize: "14px" }}>
            {deployedDateFormatted}
          </Typography>
        ),
      },
    },
  ];

  return (
    <Card
      sx={{
        p: 3,
        borderRadius: borderRadiusPx.xl,
        backgroundColor: colors.background.paper,
        border: `1px solid ${colors.dashboard.cardBorder}`,
        boxShadow: customShadows.cardFlat,
      }}
    >
      {/* Header */}
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2.5 }}>
        <Box
          sx={{
            width: 38,
            height: 38,
            borderRadius: borderRadiusPx.lg,
            backgroundColor: colors.blue[50],
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <LotteryIcon sx={{ fontSize: 22, color: colors.primary.main }} />
        </Box>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, color: colors.slate[900], fontSize: "1.1rem" }}>
            Lottery Details
          </Typography>
          <Typography variant="caption" sx={{ color: colors.slate[500], fontSize: "12px" }}>
            Smart contract parameters, schedules, fees, and winner outcome
          </Typography>
        </Box>
      </Stack>

      <Divider sx={{ mb: 2.5, borderColor: colors.dashboard.divider }} />

      {/* Symmetrical 2-Column Grid with Matched Row Heights */}
      <Grid container spacing={2}>
        {fieldPairs.map((pair, idx) => (
          <React.Fragment key={idx}>
            {/* Left Item */}
            <Grid item xs={12} md={6} sx={{ display: "flex" }}>
              <Box
                sx={{
                  p: 1.5,
                  px: 2,
                  borderRadius: borderRadiusPx.lg,
                  backgroundColor: colors.slate[50],
                  border: `1px solid ${colors.slate[200]}60`,
                  width: "100%",
                  minHeight: "74px",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  transition: "all 0.15s ease",
                  "&:hover": {
                    backgroundColor: colors.slate[100],
                    borderColor: colors.slate[300],
                  },
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                  {pair.left.icon}
                  <Typography sx={{ fontSize: "12px", color: colors.slate[500], fontWeight: 600 }}>
                    {pair.left.label}
                  </Typography>
                </Stack>
                <Box sx={{ pl: 3.25 }}>{pair.left.value}</Box>
              </Box>
            </Grid>

            {/* Right Item */}
            <Grid item xs={12} md={6} sx={{ display: "flex" }}>
              <Box
                sx={{
                  p: 1.5,
                  px: 2,
                  borderRadius: borderRadiusPx.lg,
                  backgroundColor: colors.slate[50],
                  border: `1px solid ${colors.slate[200]}60`,
                  width: "100%",
                  minHeight: "74px",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  transition: "all 0.15s ease",
                  "&:hover": {
                    backgroundColor: colors.slate[100],
                    borderColor: colors.slate[300],
                  },
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                  {pair.right.icon}
                  <Typography sx={{ fontSize: "12px", color: colors.slate[500], fontWeight: 600 }}>
                    {pair.right.label}
                  </Typography>
                </Stack>
                <Box sx={{ pl: 3.25 }}>{pair.right.value}</Box>
              </Box>
            </Grid>
          </React.Fragment>
        ))}
      </Grid>

      {/* Winner Row (Matching original winner display at bottom) */}
      <Box
        sx={{
          mt: 2.5,
          p: 2,
          px: 2.5,
          borderRadius: borderRadiusPx.lg,
          backgroundColor: hasWinner ? colors.emerald[50] : colors.slate[50],
          border: `1px solid ${hasWinner ? colors.emerald[200] : colors.slate[200]}`,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1.5,
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: borderRadiusPx.md,
              backgroundColor: hasWinner ? colors.emerald[100] : colors.slate[200],
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <TrophyIcon sx={{ fontSize: 22, color: hasWinner ? colors.emerald[700] : colors.slate[600] }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: "12px", fontWeight: 600, color: hasWinner ? colors.emerald[800] : colors.slate[500] }}>
              Winner
            </Typography>
            <Typography
              sx={{
                fontFamily: hasWinner ? "monospace" : "inherit",
                fontSize: "14px",
                fontWeight: 700,
                color: hasWinner ? colors.emerald[900] : colors.slate[700],
                wordBreak: "break-all",
              }}
            >
              {hasWinner ? lottery.lotteryWinner : "No Winner"}
            </Typography>
          </Box>
        </Stack>

        {hasWinner && (
          <Stack direction="row" spacing={0.5}>
            <Tooltip title={copiedWinner ? "Copied!" : "Copy Winner Address"} arrow>
              <IconButton
                size="small"
                onClick={() => handleCopy(lottery.lotteryWinner, "winner")}
                sx={{
                  p: 0.75,
                  color: copiedWinner ? colors.emerald[700] : colors.emerald[600],
                  bgcolor: "rgba(255, 255, 255, 0.7)",
                  "&:hover": { bgcolor: "#ffffff" },
                }}
              >
                {copiedWinner ? <CheckIcon sx={{ fontSize: 18 }} /> : <CopyIcon sx={{ fontSize: 18 }} />}
              </IconButton>
            </Tooltip>
            <Tooltip title={EXPLORER_LABEL} arrow>
              <IconButton
                size="small"
                component="a"
                href={getExplorerAddressUrl(lottery.lotteryWinner)}
                target="_blank"
                rel="noreferrer"
                sx={{
                  p: 0.75,
                  color: colors.emerald[600],
                  bgcolor: "rgba(255, 255, 255, 0.7)",
                  "&:hover": { bgcolor: "#ffffff" },
                }}
              >
                <OpenInNewIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </Stack>
        )}
      </Box>
    </Card>
  );
};

LotteryDetailsCard.propTypes = {
  lottery: PropTypes.object,
  datelist: PropTypes.object,
  winningNumbers: PropTypes.any,
};

export default LotteryDetailsCard;
